import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import { trackContactFormSubmit, trackAdsConversion } from '../utils/analytics';

const API_URL = '';

const MODEL_PHOTO_URL = 'https://customer-assets.emergentagent.com/job_0e786171-2f3a-4f49-9e53-1b5c6da47aa8/artifacts/rq28no07_model.jpg';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  posing_comfort: '',
  session_interest: '',
  age_confirmed_18plus: false,
  experience: '',
  availability: '',
  notes: '',
  website: '',
};

const BecomeAModel = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.posing_comfort) { toast.error('Please pick a posing comfort level'); return; }
    if (!form.session_interest) { toast.error('Please pick a session interest'); return; }
    if (form.posing_comfort === 'nude' && !form.age_confirmed_18plus) {
      toast.error('Please confirm you are 18 or older to apply for life-drawing work');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact/model-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
        setForm(emptyForm);
        toast.success("Application sent — we'll be in touch");
        // Fire Google Ads / GA4 conversion events
        try {
          trackContactFormSubmit('Become a Model Application');
          trackAdsConversion();
        } catch (_) { /* analytics best-effort */ }
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to submit application');
      }
    } catch {
      toast.error('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Become a Model | The Coppini Academy of Fine Art"
        description="Apply to model for figure and portrait classes at The Coppini Academy of Fine Art in San Antonio. Paid hourly. Clothed, costumed, and life-drawing sessions available."
        keywords="art model san antonio, figure model, portrait model, life drawing model"
      />

      {/* Hero */}
      <section className="bg-stone-50 border-b border-stone-200 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-stone-900 mb-3">
            Become a Model for Our Figure &amp; Portrait Classes
          </h1>
          <p className="text-base text-stone-600 max-w-2xl mx-auto">
            Help our students develop their classical training. Both clothed and nude
            (life-drawing) sessions available — paid hourly, 18+ only for nude work.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

            {/* Studio photo + about copy */}
            <div className="space-y-5" data-testid="model-page-photo-column">
              <div className="rounded-lg overflow-hidden shadow-md border border-stone-200">
                <img
                  src={MODEL_PHOTO_URL}
                  alt="Artists at The Coppini Academy of Fine Art working from a portrait model in studio"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              <div className="text-sm text-stone-700 space-y-3 leading-relaxed">
                <p>
                  Models are an essential part of our classical art training. Whether holding
                  a long pose for our portrait painters or a short gesture sequence for our
                  figure-drawing students, the role is professional and respected.
                </p>
                <p>
                  All sessions take place in our studio at <strong>115 Melrose Place, San Antonio</strong>,
                  with multiple students and a working instructor present at all times.
                  Models are paid hourly and breaks are scheduled at regular intervals.
                </p>
                <p>
                  Applications are reviewed personally by our model coordinator —
                  your information is not posted publicly.
                </p>
              </div>
            </div>

            {/* Application form */}
            <Card className="border-stone-200 shadow-sm" data-testid="model-application-card">
              <CardContent className="p-6 sm:p-8">
                {submitted ? (
                  <div className="text-center py-8" data-testid="model-application-success">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </div>
                    <h3 className="text-xl font-serif text-stone-900 mb-2">Thank you for your interest</h3>
                    <p className="text-stone-600 max-w-md mx-auto">
                      Our model coordinator will review your application and reply to you by email within a few days.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-6"
                      onClick={() => setSubmitted(false)}
                      data-testid="model-application-reset"
                    >
                      Submit another application
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="model-application-form">
                    {/* Honeypot */}
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="model-website">Website (leave blank)</label>
                      <input
                        id="model-website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={(e) => update('website', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="model-name" className="text-stone-700">Full name <span className="text-red-500">*</span></Label>
                        <Input id="model-name" value={form.name} onChange={(e) => update('name', e.target.value)} required className="mt-2" placeholder="Your name" data-testid="model-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="model-email" className="text-stone-700">Email <span className="text-red-500">*</span></Label>
                        <Input id="model-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required className="mt-2" placeholder="you@example.com" data-testid="model-email-input" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="model-phone" className="text-stone-700">Phone <span className="text-stone-400 text-xs">(optional)</span></Label>
                      <Input id="model-phone" type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-2" placeholder="(210) 555-0123" data-testid="model-phone-input" />
                    </div>

                    <div>
                      <Label className="text-stone-700">Posing comfort <span className="text-red-500">*</span></Label>
                      <div className="mt-2 space-y-2">
                        {[
                          { value: 'clothed',  label: 'Clothed only (figure or portrait)' },
                          { value: 'costumed', label: 'Costumed / draped (period costume, draped fabric, etc.)' },
                          { value: 'nude',     label: 'Nude (life drawing) — 18+ only' },
                          { value: 'discuss',  label: 'Open to discuss with the coordinator' },
                        ].map((opt) => (
                          <label
                            key={opt.value}
                            className={`flex items-start gap-2 p-3 border rounded cursor-pointer hover:bg-stone-50 ${
                              form.posing_comfort === opt.value ? 'bg-amber-50 border-amber-300' : 'border-stone-200'
                            }`}
                          >
                            <input
                              type="radio"
                              name="posing_comfort"
                              value={opt.value}
                              checked={form.posing_comfort === opt.value}
                              onChange={() => update('posing_comfort', opt.value)}
                              data-testid={`posing-${opt.value}`}
                            />
                            <span className="text-sm text-stone-700">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {form.posing_comfort === 'nude' && (
                      <label className="flex items-start gap-2 p-3 border border-amber-300 bg-amber-50 rounded text-sm text-amber-900" data-testid="age-confirm-block">
                        <input
                          type="checkbox"
                          checked={form.age_confirmed_18plus}
                          onChange={(e) => update('age_confirmed_18plus', e.target.checked)}
                          className="mt-1"
                          data-testid="age-confirm-checkbox"
                        />
                        <span>I confirm I am <strong>18 years of age or older</strong>. (Required for life-drawing applications.)</span>
                      </label>
                    )}

                    <div>
                      <Label htmlFor="session-interest" className="text-stone-700">Session interest <span className="text-red-500">*</span></Label>
                      <Select value={form.session_interest} onValueChange={(v) => update('session_interest', v)}>
                        <SelectTrigger id="session-interest" className="mt-2" data-testid="session-interest-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="figure">Figure</SelectItem>
                          <SelectItem value="portrait">Portrait</SelectItem>
                          <SelectItem value="either">Either / Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="model-experience" className="text-stone-700">Prior experience <span className="text-stone-400 text-xs">(optional)</span></Label>
                        <Select value={form.experience} onValueChange={(v) => update('experience', v)}>
                          <SelectTrigger id="model-experience" className="mt-2" data-testid="experience-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No prior experience</SelectItem>
                            <SelectItem value="some">Some experience</SelectItem>
                            <SelectItem value="experienced">Experienced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="model-availability" className="text-stone-700">Availability <span className="text-stone-400 text-xs">(optional)</span></Label>
                        <Input id="model-availability" value={form.availability} onChange={(e) => update('availability', e.target.value)} className="mt-2" placeholder="Days / evenings / weekends" data-testid="model-availability-input" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="model-notes" className="text-stone-700">Anything else? <span className="text-stone-400 text-xs">(optional)</span></Label>
                      <Textarea id="model-notes" value={form.notes} onChange={(e) => update('notes', e.target.value)} className="mt-2 min-h-[90px]" placeholder="Questions, scheduling constraints, anything you'd like our coordinator to know." data-testid="model-notes-input" />
                    </div>

                    <Button type="submit" disabled={submitting} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="submit-model-application">
                      {submitting ? 'Sending…' : 'Send Application'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BecomeAModel;
